/**
 * Inventory Intelligence Dashboard
 *
 * 6 insight categories computed from MSR data:
 *  1. Revenue at Risk — high-revenue SKUs approaching stockout
 *  2. Sell-Through vs Expiry — will stock expire before it sells?
 *  3. Dead Stock — zero velocity, capital tied up
 *  4. Demand Trends — D7 vs D30 acceleration/deceleration
 *  5. Reorder Recommendations — lead-time-aware ordering guidance
 *  6. Cash Efficiency — stock value by ROD bands
 *
 * Uses the brand already selected in the top nav (BrandContext).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  AccountBalance as CashIcon,
  ShowChart as TrendIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { getBrandAlerts } from '../../services/inventoryService';

// ============================================================================
// HELPERS
// ============================================================================

const fmt = (n) => n == null ? '-' : Number(n).toLocaleString();
const fmtCurrency = (n) => n == null ? '-' : '$' + Number(n).toLocaleString();
const fmtPct = (n) => n == null ? '-' : (n >= 0 ? '+' : '') + n + '%';
const fmtROD = (d) => d == null || d >= 9999 ? 'No sales' : d <= 0 ? 'Out!' : d + 'd';

const SECTION_CONFIG = {
  revenueAtRisk:        { title: 'Revenue at Risk',        icon: MoneyIcon,    color: '#e65100', bg: '#fff3e0', border: '#ff9800' },
  expiryRace:           { title: 'Sell-Through vs Expiry',  icon: ScheduleIcon, color: '#b71c1c', bg: '#ffebee', border: '#f44336' },
  deadStock:            { title: 'Dead Stock',              icon: InventoryIcon,color: '#424242', bg: '#f5f5f5', border: '#9e9e9e' },
  demandTrends:         { title: 'Demand Trends',           icon: TrendIcon,    color: '#1565c0', bg: '#e3f2fd', border: '#2196f3' },
  reorderRecommendations: { title: 'Reorder Recommendations', icon: ShippingIcon, color: '#e65100', bg: '#fff3e0', border: '#ff9800' },
  cashEfficiency:       { title: 'Cash Efficiency',         icon: CashIcon,     color: '#2e7d32', bg: '#e8f5e9', border: '#4caf50' },
};

// ============================================================================
// KPI CARD
// ============================================================================

function KpiCard({ title, primary, secondary, icon: Icon, color, bg, border, onClick }) {
  const hasValue = primary !== '0' && primary !== '$0' && primary !== '-';
  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: `4px solid ${border}`,
        bgcolor: hasValue ? bg : 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: hasValue ? color : 'text.disabled', mt: 0.25 }}>
              {primary}
            </Typography>
            <Typography variant="caption" color="text.secondary">{secondary}</Typography>
          </Box>
          <Icon sx={{ color: hasValue ? border : 'action.disabled', fontSize: 28, mt: 0.5 }} />
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COLLAPSIBLE SECTION WRAPPER
// ============================================================================

// `count` is the FULL population (data.fullCounts.*). `shown` is how many rows the table below
// actually renders, which the service caps at 25-30 (inventory-summary-service.js insights.*
// .slice(0, 25)). When they differ the badge alone reads as if the table were complete, so say so.
function InsightSection({ sectionKey, count, shown, expanded, onToggle, children }) {
  const cfg = SECTION_CONFIG[sectionKey];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const isTruncated = typeof shown === 'number' && shown > 0 && shown < count;
  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{ p: 1.5, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ color: cfg.color, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="bold">{cfg.title}</Typography>
          <Chip size="small" label={count} sx={{ height: 20, bgcolor: cfg.bg, color: cfg.color, fontWeight: 700 }} />
          {isTruncated && (
            <Typography variant="caption" color="text.secondary">
              showing {shown} of {count}
            </Typography>
          )}
        </Box>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 0 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

// ============================================================================
// CASH EFFICIENCY BAR
// ============================================================================

function CashBar({ bands }) {
  if (!bands) return null;
  const order = ['lean', 'healthy', 'overstocked', 'excess', 'dead'];
  const colors = { lean: '#4caf50', healthy: '#8bc34a', overstocked: '#ff9800', excess: '#f44336', dead: '#616161' };
  const total = order.reduce((s, k) => s + (bands[k]?.value || 0), 0);
  if (total === 0) return <Typography variant="caption" color="text.secondary">No stock data</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', height: 18, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
        {order.map(k => {
          const pct = (bands[k]?.value || 0) / total * 100;
          if (pct < 1) return null;
          return <Tooltip key={k} title={`${bands[k]?.label}: ${fmtCurrency(bands[k]?.value)} (${Math.round(pct)}%)`}>
            <Box sx={{ width: `${pct}%`, bgcolor: colors[k], minWidth: pct > 2 ? 4 : 0 }} />
          </Tooltip>;
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {order.map(k => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors[k] }} />
            <Typography variant="caption" color="text.secondary">
              {bands[k]?.label}: <strong>{fmtCurrency(bands[k]?.value)}</strong> ({bands[k]?.skuCount} SKUs)
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ============================================================================
// TREND CHIP
// ============================================================================

function TrendChip({ trend, changePercent }) {
  const cfg = {
    accelerating: { label: 'Accelerating', color: 'success', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    new_demand:   { label: 'New Demand',    color: 'success', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    growing:      { label: 'Growing',       color: 'info',    icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    declining:    { label: 'Declining',     color: 'warning',  icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
    dropping:     { label: 'Dropping',      color: 'error',   icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  };
  const c = cfg[trend] || { label: trend, color: 'default', icon: <TrendingFlatIcon sx={{ fontSize: 14 }} /> };
  return <Chip size="small" icon={c.icon} label={changePercent != null ? `${c.label} ${fmtPct(changePercent)}` : c.label} color={c.color} sx={{ height: 22 }} />;
}

// ============================================================================
// EXPIRY STATUS CHIP
// ============================================================================

function ExpiryStatusChip({ status }) {
  const cfg = {
    will_sell_through: { label: 'Will Sell Through', color: 'success' },
    tight:             { label: 'Tight',             color: 'warning' },
    will_expire:       { label: 'Will Expire Unsold', color: 'error' },
  };
  const c = cfg[status] || { label: status, color: 'default' };
  return <Chip size="small" label={c.label} color={c.color} sx={{ height: 22 }} />;
}

// ============================================================================
// COMPACT TABLE
// ============================================================================

function CompactTable({ columns, rows, onRowClick }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableCell key={col.key} sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.75, whiteSpace: 'nowrap' }} align={col.align || 'left'}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={row.sku || idx}
              hover
              sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map(col => (
                <TableCell key={col.key} sx={{ fontSize: '0.8rem', py: 0.5 }} align={col.align || 'left'}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function InventorySummary() {
  const navigate = useNavigate();
  const { selectedBrand, brands, selectBrand } = useBrand();

  // Brand selector — this screen is scoped to the globally-selected brand via
  // BrandContext; surface it explicitly so the scope is visible and changeable.
  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) selectBrand(brand);
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({
    revenueAtRisk: true,
    expiryRace: false,
    deadStock: false,
    demandTrends: false,
    reorderRecommendations: false,
    cashEfficiency: false,
  });

  const loadData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getBrandAlerts(selectedBrand.id);
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load intelligence data');
      }
    } catch (err) {
      setError(err.message || 'Failed to load intelligence data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const goMSR = () => navigate(`/reports/run-rates?brand=${selectedBrand?.id}`);
  const goStock = (row) => navigate(`/inventory/stock?brand=${selectedBrand?.id}${row?.sku ? `&search=${encodeURIComponent(row.sku)}` : ''}`);

  // No brand selected
  if (!selectedBrand?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select a brand from the top navigation to view inventory intelligence.</Alert>
      </Box>
    );
  }

  const s = data?.summary || {};
  const ins = data?.insights || {};
  const fc = data?.fullCounts || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Inventory Intelligence</Typography>
          {data && (
            <Typography variant="caption" color="text.secondary">
              {s.totalSkus} SKUs tracked &middot; {fmtCurrency(s.totalStockValue)} stock value
              {data.brand30dRevenue > 0 && <> &middot; {fmtCurrency(data.brand30dRevenue)} 30-day revenue</>}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Brand</InputLabel>
            <Select
              value={selectedBrand?.id || ''}
              onChange={handleBrandChange}
              label="Select Brand"
            >
              {brands.map(brand => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh (recomputes from MSR — may take 10-30s)">
            <IconButton onClick={loadData} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">Analyzing inventory intelligence...</Typography>
          <Typography variant="caption" color="text.secondary">Computing run rates, expiry projections, and demand trends</Typography>
        </Box>
      )}

      {/* Content */}
      {!loading && data && (
        <>
          {/* ---- KPI Cards ---- */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Revenue at Risk"
                primary={s.revenueAtRiskCount > 0 ? fmtCurrency(s.revenueAtRiskValue) : '0'}
                secondary={s.revenueAtRiskCount > 0 ? `${s.revenueAtRiskCount} SKUs approaching stockout` : 'No stockout risk'}
                onClick={() => toggle('revenueAtRisk')}
                {...SECTION_CONFIG.revenueAtRisk}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Expiry Risk"
                primary={s.willExpireCount > 0 ? `${s.willExpireCount}` : '0'}
                secondary={s.willExpireCount > 0
                  ? `${fmtCurrency(s.expiryValueAtRisk)} value at risk` + (s.tightCount > 0 ? ` + ${s.tightCount} tight` : '')
                  : s.tightCount > 0 ? `${s.tightCount} tight (borderline)` : 'All batches selling through'}
                onClick={() => toggle('expiryRace')}
                {...SECTION_CONFIG.expiryRace}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Dead Stock"
                primary={s.deadStockCount > 0 ? fmtCurrency(s.deadStockValue) : '0'}
                secondary={s.deadStockCount > 0 ? `${s.deadStockCount} SKUs, no sales in 90 days` : 'No dead stock detected'}
                onClick={() => toggle('deadStock')}
                {...SECTION_CONFIG.deadStock}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Demand Trends"
                primary={s.acceleratingCount > 0 || s.decliningCount > 0
                  ? `${s.acceleratingCount} / ${s.decliningCount}`
                  : '-'}
                secondary={s.acceleratingCount > 0 || s.decliningCount > 0
                  ? `${s.acceleratingCount} accelerating, ${s.decliningCount} declining`
                  : 'All SKUs stable'}
                onClick={() => toggle('demandTrends')}
                {...SECTION_CONFIG.demandTrends}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Reorder Now"
                primary={`${s.reorderCriticalCount || 0}`}
                secondary={s.reorderCriticalCount > 0
                  ? `Critical` + (s.reorderWarningCount > 0 ? ` + ${s.reorderWarningCount} warning` : '')
                  : s.reorderWarningCount > 0 ? `${s.reorderWarningCount} approaching reorder point` : 'No reorders needed'}
                onClick={() => toggle('reorderRecommendations')}
                {...SECTION_CONFIG.reorderRecommendations}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Capital Allocation"
                primary={fmtCurrency(s.totalStockValue)}
                secondary="Stock value by days-of-supply band"
                onClick={() => toggle('cashEfficiency')}
                {...SECTION_CONFIG.cashEfficiency}
              />
            </Grid>
          </Grid>

          {/* ---- 1. Revenue at Risk ---- */}
          {(ins.revenueAtRisk?.length > 0 || expanded.revenueAtRisk) && (
            <InsightSection sectionKey="revenueAtRisk" count={fc.revenueAtRisk || 0} shown={ins.revenueAtRisk?.length} expanded={expanded.revenueAtRisk} onToggle={() => toggle('revenueAtRisk')}>
              {ins.revenueAtRisk?.length > 0 ? (
                <CompactTable
                  onRowClick={goStock}
                  columns={[
                    { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                    { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{v}</Typography> },
                    { key: 'rodDays', label: 'ROD', align: 'right', render: (v) => <Chip size="small" label={fmtROD(v)} color={v < 30 ? 'error' : v < 60 ? 'warning' : 'default'} sx={{ height: 20, fontWeight: 700 }} /> },
                    { key: 'dailyRate', label: 'Rate/day', align: 'right', render: (v) => fmt(v) },
                    { key: 'soh', label: 'SOH', align: 'right', render: (v) => fmt(v) },
                    { key: 'revenueShare', label: 'Rev %', align: 'right', render: (v) => v + '%' },
                    { key: 'monthlyRevenue', label: 'Monthly Rev', align: 'right', render: (v) => fmtCurrency(v) },
                    { key: 'rodDate', label: 'Stockout Date', render: (v) => v || '-' },
                  ]}
                  rows={ins.revenueAtRisk}
                />
              ) : <Typography sx={{ p: 2 }} color="text.secondary">No SKUs at risk of stocking out within 90 days.</Typography>}
            </InsightSection>
          )}

          {/* ---- 2. Sell-Through vs Expiry ---- */}
          {(ins.expiryRace?.length > 0 || expanded.expiryRace) && (
            <InsightSection sectionKey="expiryRace" count={fc.expiryRace || 0} shown={ins.expiryRace?.length} expanded={expanded.expiryRace} onToggle={() => toggle('expiryRace')}>
              {ins.expiryRace?.length > 0 ? (
                <CompactTable
                  onRowClick={goStock}
                  columns={[
                    { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                    { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{v}</Typography> },
                    { key: 'status', label: 'Status', render: (v) => <ExpiryStatusChip status={v} /> },
                    { key: 'soh', label: 'SOH', align: 'right', render: (v) => fmt(v) },
                    { key: 'dailyRate', label: 'Rate/day', align: 'right', render: (v) => fmt(v) },
                    { key: 'daysToFirstExpiry', label: 'Days to Expiry', align: 'right', render: (v) => fmt(v) },
                    { key: 'totalExpiring', label: 'Expiring', align: 'right', render: (v) => fmt(v) },
                    { key: 'unitsAtRisk', label: 'Units at Risk', align: 'right', render: (v, row) => v > 0 ? <Typography color="error" variant="body2" fontWeight={700}>{fmt(v)}</Typography> : '-' },
                    { key: 'valueAtRisk', label: 'Value at Risk', align: 'right', render: (v) => v > 0 ? fmtCurrency(v) : '-' },
                  ]}
                  rows={ins.expiryRace}
                />
              ) : <Typography sx={{ p: 2 }} color="text.secondary">No expiring batches tracked.</Typography>}
            </InsightSection>
          )}

          {/* ---- 3. Dead Stock ---- */}
          {(ins.deadStock?.length > 0 || expanded.deadStock) && (
            <InsightSection sectionKey="deadStock" count={fc.deadStock || 0} shown={ins.deadStock?.length} expanded={expanded.deadStock} onToggle={() => toggle('deadStock')}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 1 }}>
                Dead stock = in stock but <b>no units sold on any channel (Amazon + Shopify) in the last 90 days</b> — ranked by inventory value tied up.
              </Typography>
              {ins.deadStock?.length > 0 ? (
                <CompactTable
                  onRowClick={goStock}
                  columns={[
                    { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                    { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{v}</Typography> },
                    { key: 'soh', label: 'SOH', align: 'right', render: (v) => fmt(v) },
                    { key: 'value', label: 'Value Tied Up', align: 'right', render: (v) => <Typography fontWeight={700} variant="body2">{fmtCurrency(v)}</Typography> },
                    { key: 'dailyRate', label: 'Rate/day', align: 'right', render: (v) => v > 0 ? fmt(v) : <Typography color="error" variant="body2">0</Typography> },
                    { key: 'rodDays', label: 'ROD', align: 'right', render: (v) => fmtROD(v) },
                    { key: 'category', label: 'Category', render: (v) => v || '-' },
                  ]}
                  rows={ins.deadStock}
                />
              ) : <Typography sx={{ p: 2 }} color="text.secondary">No dead stock detected. All SKUs have recent sales.</Typography>}
            </InsightSection>
          )}

          {/* ---- 4. Demand Trends ---- */}
          {(ins.demandTrends?.length > 0 || expanded.demandTrends) && (
            <InsightSection sectionKey="demandTrends" count={fc.demandTrends || 0} shown={ins.demandTrends?.length} expanded={expanded.demandTrends} onToggle={() => toggle('demandTrends')}>
              {ins.demandTrends?.length > 0 ? (
                <CompactTable
                  onRowClick={goStock}
                  columns={[
                    { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                    { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{v}</Typography> },
                    { key: 'trend', label: 'Trend', render: (v, row) => <TrendChip trend={v} changePercent={row.changePercent} /> },
                    { key: 'd7Daily', label: 'D7/day', align: 'right', render: (v) => fmt(v) },
                    { key: 'd14Daily', label: 'D14/day', align: 'right', render: (v) => fmt(v) },
                    { key: 'd30Daily', label: 'D30/day', align: 'right', render: (v) => fmt(v) },
                    { key: 'soh', label: 'SOH', align: 'right', render: (v) => fmt(v) },
                    { key: 'rodDays', label: 'ROD', align: 'right', render: (v) => fmtROD(v) },
                  ]}
                  rows={ins.demandTrends}
                />
              ) : <Typography sx={{ p: 2 }} color="text.secondary">All SKUs have stable demand (D7 within 15% of D30).</Typography>}
            </InsightSection>
          )}

          {/* ---- 5. Reorder Recommendations ---- */}
          {(ins.reorderRecommendations?.length > 0 || expanded.reorderRecommendations) && (
            <InsightSection sectionKey="reorderRecommendations" count={fc.reorderRecommendations || 0} shown={ins.reorderRecommendations?.length} expanded={expanded.reorderRecommendations} onToggle={() => toggle('reorderRecommendations')}>
              {ins.reorderRecommendations?.length > 0 ? (
                <CompactTable
                  onRowClick={goStock}
                  columns={[
                    { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                    { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{v}</Typography> },
                    { key: 'urgency', label: 'Urgency', render: (v) => <Chip size="small" label={v === 'critical' ? 'CRITICAL' : 'Warning'} color={v === 'critical' ? 'error' : 'warning'} sx={{ height: 20, fontWeight: 700 }} /> },
                    { key: 'rodDays', label: 'ROD', align: 'right', render: (v) => <Chip size="small" label={fmtROD(v)} color={v < 30 ? 'error' : 'default'} sx={{ height: 20 }} /> },
                    { key: 'leadTimeDays', label: 'Lead Time', align: 'right', render: (v) => v ? `${v}d` : <Typography variant="caption" color="text.secondary">Unknown</Typography> },
                    { key: 'orderByDate', label: 'Order By', render: (v) => v ? <Typography variant="body2" fontWeight={700} color="error">{v}</Typography> : '-' },
                    { key: 'suggestedQty', label: 'Suggested Qty', align: 'right', render: (v) => fmt(v) },
                    { key: 'moq', label: 'MOQ', align: 'right', render: (v) => v ? fmt(v) : '-' },
                    { key: 'revenueShare', label: 'Rev %', align: 'right', render: (v) => v + '%' },
                  ]}
                  rows={ins.reorderRecommendations}
                />
              ) : <Typography sx={{ p: 2 }} color="text.secondary">No reorders needed. All SKUs have sufficient stock.</Typography>}
            </InsightSection>
          )}

          {/* ---- 6. Cash Efficiency ---- */}
          {expanded.cashEfficiency !== undefined && (
            <InsightSection sectionKey="cashEfficiency" count={fc.cashEfficiency || 0} shown={ins.cashEfficiency?.items?.length} expanded={expanded.cashEfficiency} onToggle={() => toggle('cashEfficiency')}>
              <Box sx={{ p: 2 }}>
                <CashBar bands={s.cashBands} />
              </Box>
              {ins.cashEfficiency?.items?.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="caption" sx={{ px: 2, pt: 1, display: 'block' }} color="text.secondary" fontWeight={600}>
                    Overstocked, Excess &amp; Dead Capital (sorted by value)
                  </Typography>
                  <CompactTable
                    onRowClick={goStock}
                    columns={[
                      { key: 'sku', label: 'SKU', render: (v) => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{v}</Typography> },
                      { key: 'name', label: 'Product', render: (v) => <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{v}</Typography> },
                      { key: 'band', label: 'Band', render: (v) => {
                        const colors = { overstocked: 'warning', excess: 'error', dead: 'default' };
                        const labels = { overstocked: '90-180d', excess: '180-365d', dead: '365+' };
                        return <Chip size="small" label={labels[v] || v} color={colors[v] || 'default'} sx={{ height: 20 }} />;
                      }},
                      { key: 'soh', label: 'SOH', align: 'right', render: (v) => fmt(v) },
                      { key: 'value', label: 'Value', align: 'right', render: (v) => <Typography fontWeight={700} variant="body2">{fmtCurrency(v)}</Typography> },
                      { key: 'dailyRate', label: 'Rate/day', align: 'right', render: (v) => fmt(v) },
                      { key: 'rodDays', label: 'ROD', align: 'right', render: (v) => fmtROD(v) },
                    ]}
                    rows={ins.cashEfficiency.items}
                  />
                </>
              )}
            </InsightSection>
          )}

          {/* ---- Data Quality ---- */}
          {ins.negative?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>{ins.negative.length} SKU{ins.negative.length > 1 ? 's' : ''} with negative stock</strong> (data quality issue):
              {' '}{ins.negative.slice(0, 5).map(n => n.sku).join(', ')}
              {ins.negative.length > 5 && ` and ${ins.negative.length - 5} more`}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}

export default InventorySummary;
